import { Alert, AlertDescription, Badge, Button, cn } from "@beweegbaas/ui";
import { createFileRoute, getRouteApi, Outlet } from "@tanstack/react-router";
import { useServerFn } from "#/modules/util.hooks/use-server-fn";
import { containerStyles, Page } from "#/components/page";
import { TabsNavigation } from "#/components/tabs-navigation";
import { ImportFileDropUpload } from "#/modules/school.import/components/import-file-drop-upload";
import { ImportPreferences } from "#/modules/school.import/components/import-preferences";
import { ImportStepper } from "#/modules/school.import/components/import-stepper";
import { ImportReviewContext } from "#/modules/school.import/import-review-context";
import { getImportReviewData, submitCancelImport } from "#/modules/school.import/import.functions";
import { useImportData } from "#/modules/school.import/use-import-data";
import { m } from "#/paraglide/messages";

export const Route = createFileRoute(
  "/$locale/{-$schoolyear}/_authenticated/$school/detail/import/$type/review",
)({
  loader: ({ params }) =>
    getImportReviewData({
      data: { school: params.school, type: params.type, locale: params.locale },
    }),
  component: ImportReviewLayout,
});

const importLayoutRoute = getRouteApi(
  "/$locale/{-$schoolyear}/_authenticated/$school/detail/import/$type",
);

function ImportReviewLayout() {
  const loaderData = Route.useLoaderData();
  const { currentSchooljaar } = importLayoutRoute.useLoaderData();
  const { locale, school, type } = Route.useParams();
  const submitCancelFn = useServerFn(submitCancelImport);
  const schoolYear = `${currentSchooljaar.fromYear}-${currentSchooljaar.toYear}`;

  return (
    <ImportReviewContext.Provider value={loaderData}>
      <Page.Header className="py-6!">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <Page.Title>Importeren</Page.Title>
            <Badge>{schoolYear}</Badge>
          </div>
          <p className="text-muted-foreground text-sm">
            Controleer de gegevens uit je bestand en rond de import af.
          </p>
        </div>
        <Page.Header.Actions>
          <ImportPreferences placement="bottom" />
        </Page.Header.Actions>
      </Page.Header>
      <Page.Body>
        <ImportReviewContent onCancel={() => submitCancelFn({ data: { locale, school, type } })} />
      </Page.Body>
    </ImportReviewContext.Provider>
  );
}

function ImportReviewContent({ onCancel }: { onCancel: () => void }) {
  const importData = useImportData();

  return (
    <div className={cn(containerStyles({ container: "default" }))}>
      <Page.SidebarLayout>
        <div className="mb-4 flex flex-col justify-start gap-4">
          <TabsNavigation.List>
            <ImportStepper importData={importData} className="w-full" />
          </TabsNavigation.List>
          <Outlet />
        </div>
        <Page.Sidebar className="flex flex-col gap-4 self-start">
          <Button variant="secondary" type="button" className="grow" onClick={onCancel}>
            Annuleer import
          </Button>
          <ImportFileDropUpload
            label={
              <h6 className="mb-2 text-center font-normal text-sm! font-sans leading-normal">
                Fouten opgelost? <br /> Upload je bestand opnieuw.
              </h6>
            }
          />
          <Alert variant="muted">
            <IconTablerAlertTriangle className="size-4" />
            <AlertDescription>{m["import.student_number_notice"]()}</AlertDescription>
          </Alert>
        </Page.Sidebar>
      </Page.SidebarLayout>
    </div>
  );
}
